import { initializeApp } from 'firebase/app';
import {
    AuthProvider,
    confirmPasswordReset,
    createUserWithEmailAndPassword,
    FacebookAuthProvider,
    getAuth,
    GoogleAuthProvider,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
    UserCredential,
    verifyPasswordResetCode
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CreateUserAuth } from './api/users';
import { client } from './index';

export const functionsUrl = 'https://us-central1-pathmatch-production.cloudfunctions.net';
// export const functionsUrl =
//   "http://localhost:5000/pathmatch-production/us-central1";

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: 'AIzaSyBvAeSDWrZkIEupbv2OVD3OeaF-vAsIpn0',
    authDomain: 'pathmatch-production.firebaseapp.com',
    databaseURL: 'https://pathmatch-production.firebaseio.com',
    projectId: 'pathmatch-production',
    storageBucket: 'pathmatch-production.appspot.com',
    messagingSenderId: '418619606240',
    appId: '1:418619606240:web:4e60d57dc20f9476bcbc85'
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const database = getFirestore(app);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();

export function signInWithProvider(
    provider: AuthProvider,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    profileId: string | null,
    cid: string | null
) {
    signInWithPopup(auth, provider)
        .then(async (userCredential) => {
            const token = await userCredential.user.getIdToken();
            await CreateUserAuth(
                token,
                userCredential.user.email ?? '',
                userCredential.user.uid ?? ''
            );
        })
        .catch((error) => {
            console.log(error);
            setErrorMessage(error.message);
        });
}

export function signInWithEmailAndPasswordHelper(
    email: string,
    password: string,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>
) {
    signInWithEmailAndPassword(auth, email, password).catch((error) => {
        console.error(error);
        setErrorMessage(error.message);
    });
}

export async function logoutHelper() {
    client.clearStore()
    sessionStorage.removeItem('accessToken');
    const sign_out= await signOut(auth);
    console.log(sign_out);
}

export async function signUpWithEmailAndPasswordHelper(
    email: string,
    password: string,
    profileId: string | null,
    cid: string | null,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>
): Promise<UserCredential> {
    const data = await createUserWithEmailAndPassword(auth, email, password)
    return data;
}
        

export function resetPassword(
    email: string,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    setDone: React.Dispatch<React.SetStateAction<boolean>>
) {
    sendPasswordResetEmail(auth, email)
        .then(() => {
            setErrorMessage('');
            setDone(true);
        })
        .catch((error) => {
            console.error(error);
            setErrorMessage(error.message);
        });
}

export function handleResetPassword(
    actionCode: string,
    newPassword: string,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    setDone: React.Dispatch<React.SetStateAction<boolean>>
) {
    verifyPasswordResetCode(auth, actionCode)
        .then((email) => {
            confirmPasswordReset(auth, actionCode, newPassword)
                .then(() => {
                    setErrorMessage('');
                    setDone(true);
                })
                .catch((error) => {
                    console.error(error);
                    setErrorMessage(error.message);
                });
        })
        .catch((error) => {
            console.error(error);
            setErrorMessage(error.message);
        });
}

interface VideoUploadParams {
    file: File;
    name: string;
    profileId: string;
}

export function useVideoUpload() {
    const queryClient = useQueryClient();

    return useMutation(
        (profile: VideoUploadParams) => {
            var data = new FormData();
            data.append('file', profile.file);
            data.append('name', profile.name);
            return fetch(`${functionsUrl}/uploadConvertVideo/${profile.profileId}/uploadVideo`, {
                method: 'POST',
                body: data
            });
        },
        {
            onSettled: (data, error, variables) => {
                queryClient.invalidateQueries(['profile', variables.profileId]);
            },
            onSuccess: () => {
                'success';
            }
        }
    );
}

type ITranscodeStatus = {
    status: string;
};

export function useCheckVimeoTranscode(pid: string | undefined) {
    return useQuery<ITranscodeStatus, Error>(['transcodeData', pid], async () => {
        if (pid === '') {
            return;
        }
        const response = await (
            await fetch(`${functionsUrl}/checkTranscodeVideo/${pid}/transcodeStatus`, {
                method: 'GET',
                headers: {
                    accept: 'application/json'
                }
            })
        ).json();
        return response;
    });
}

export interface IEditProfile {
    accessToken: string;
    data: any;
}
export function useEditProfile(profileId: string) {
    const queryClient = useQueryClient();
    return useMutation(
        (input: IEditProfile) => {
            const body = JSON.stringify(input.data);
            return fetch(`${functionsUrl}/editProfile/edit`, {
                method: 'PATCH',
                body: body,
                headers: {
                    Authorization: 'Bearer ' + input.accessToken,
                    'Content-Type': 'application/json'
                }
            });
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(['profile', profileId]);
                queryClient.setQueryData(['profile', profileId], data);
            }
        }
    );
}

export interface IProfile {
    name?: string;
    firstName?: string;
    lastName?: string;
    pronouns?: string;
    'elevator-pitch-video-url-set'?: boolean;
    'elevator-pitch-video-url'?: IVideoURL;
    'one-line-description'?: string;
    school?: string;
    major?: string;
    gpa?: string;
    'graduation-date'?: string;
    location?: Location;
    strengths?: string[];
    tools?: string[];
    'experience-item-1-title'?: string;
    'experience-item-1-description'?: string;
    'experience-item-1-employer'?: string;
    'experience-item-1-date-range'?: string;
    'experience-item-2-title'?: string;
    'experience-item-2-description'?: string;
    'experience-item-2-employer'?: string;
    'experience-item-2-date-range'?: string;
    'experience-item-3-title'?: string;
    'experience-item-3-description'?: string;
    'experience-item-3-employer'?: string;
    'experience-item-3-date-range'?: string;
    'experience-item-4-title'?: string;
    'experience-item-4-description'?: string;
    'experience-item-4-employer'?: string;
    'experience-item-4-date-range'?: string;
    'activity-1-date-range'?: string;
    'activity-1-name'?: string;
    'activity-1-title'?: string;
    'activity-2-date-range'?: string;
    'activity-2-name'?: string;
    'activity-2-title'?: string;
    'activity-3-date-range'?: string;
    'activity-3-name'?: string;
    'activity-3-title'?: string;
    'activity-4-date-range'?: string;
    'activity-4-name'?: string;
    'activity-4-title'?: string;
    'portfolio-link'?: string;
    resume?: string;
    slug?: string;
    email?: string;
    hidden?: boolean;
    profileId?: string;
    isLegal?: boolean;
}

export interface IVideoURL {
    url?: string;
}

export interface Location {
    'san-francisco': boolean;
    'san-diego': boolean;
    philadelphia: boolean;
    boston: boolean;
    chicago: boolean;
    'new-york': boolean;
    seattle: boolean;
    'washington-dc': boolean;
    portland: boolean;
    denver: boolean;
    austin: boolean;
    'los-angeles': boolean;
    indianapolis: boolean;
    phoenix: boolean;
    dallas: boolean;
    atlanta: boolean;
    remote: boolean;
    houston: boolean;
}

export interface Dashboard {
    paths: CareerPath[];
}

export interface CareerPath {
    name: string;
    'hero-image': { url: string };
    summary: string;
    slug: string;
    'rate-typeform-link': string;
}
